import React, { useEffect, useState } from "react";
import { Users, MessageSquare, Wifi, Lightbulb, Send, Trash2, ShieldCheck, Loader2, Image as ImageIcon, Paperclip, HardDrive, Reply, ExternalLink, Save, KeyRound, X, BadgeCheck, SlidersHorizontal, AlertTriangle, Ban, Check, Sparkles, Bell, Download, RotateCcw, Smartphone, Mail, PhoneCall, Database, UploadCloud, UserPlus, PenSquare } from "lucide-react";
import Modal from "@/components/chat/Modal";
import api from "@/lib/api";
import { uploadFileChunked, fileUrl } from "@/lib/uploads";
import { useI18n, LANGS } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { allowedSections, ADMIN_SECTIONS } from "@/lib/roles";
import RingtonesAdmin from "@/components/chat/RingtonesAdmin";
import { toast } from "sonner";

const MEDIA_TYPES = ["image", "video", "audio", "document"];
const fmtBytes = (b) => { if (!b) return "0 B"; const u = ["B", "KB", "MB", "GB"]; const i = Math.floor(Math.log(b) / Math.log(1024)); return `${(b / 1024 ** i).toFixed(1)} ${u[i]}`; };
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const avatarUrl = (id) => `${BACKEND}/api/avatar/${id}`;
const fmtJoin = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return "—"; } };

function Bars({ data, label }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div>
      <p className="text-xs text-[var(--rc-muted)] mb-2">{label}</p>
      <div className="flex items-end gap-1 h-28">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.count}`}>
            <div className="w-full rounded-t bg-[var(--rc-gold)]/70 hover:bg-[var(--rc-gold)] transition-colors" style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? 3 : 0 }} />
            {i % 2 === 0 && <span className="text-[8px] text-[var(--rc-muted)]">{d.day}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDialog({ onClose }) {
  const { t, lang } = useI18n();
  const fr = lang === "fr";
  const { user } = useAuth();
  const sections = allowedSections(user);
  const canManageRoles = user?.role === "admin";
  const has = (s) => sections.includes(s);
  const [tab, setTab] = useState(() => (sections.includes("overview") ? "overview" : (sections[0] || "overview")));
  const [permUserId, setPermUserId] = useState(null);
  const [an, setAn] = useState(null);
  const [users, setUsers] = useState([]);
  const [sugs, setSugs] = useState([]);
  const [bText, setBText] = useState("");
  const [bAtt, setBAtt] = useState(null); // {url,type,name}
  const [busy, setBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  // Admin-managed demo accounts
  const [demoAccounts, setDemoAccounts] = useState(null);
  const [demoForm, setDemoForm] = useState(null); // null | {id?, name, email, password, avatar, about, social_enabled}
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoAvatarUp, setDemoAvatarUp] = useState(0); // avatar upload progress %
  const [demoPostFor, setDemoPostFor] = useState(null); // account to compose a post for
  const [demoPostText, setDemoPostText] = useState("");
  const [demoPostImg, setDemoPostImg] = useState("");
  const [demoPostImgUp, setDemoPostImgUp] = useState(0);
  const [demoPostBusy, setDemoPostBusy] = useState(false);
  const loadDemoAccounts = () => api.get("/admin/demo-accounts").then(({ data }) => setDemoAccounts(data)).catch(() => setDemoAccounts([]));

  const uploadDemoPostImg = async (file) => {
    if (!file) return;
    setDemoPostImgUp(1);
    try {
      const { uploadFileDirect } = await import("@/lib/uploads");
      const { url } = await uploadFileDirect(file, (p) => setDemoPostImgUp(Math.max(p, 1)), "post");
      setDemoPostImg(url);
    } catch { toast.error(fr ? "Échec de l'envoi de l'image" : "Image upload failed"); }
    finally { setDemoPostImgUp(0); }
  };

  const submitDemoPost = async () => {
    if (!demoPostText.trim() && !demoPostImg) {
      toast.error(fr ? "Ajoutez du texte ou une image" : "Add text or an image");
      return;
    }
    setDemoPostBusy(true);
    try {
      const media = demoPostImg ? [{ type: "image", url: demoPostImg }] : [];
      await api.post(`/admin/demo-accounts/${demoPostFor.id}/post`, { text: demoPostText.trim(), media, visibility: "public" });
      toast.success(fr ? "Publié" : "Posted");
      setDemoPostFor(null); setDemoPostText(""); setDemoPostImg("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (fr ? "Échec de la publication" : "Post failed"));
    } finally { setDemoPostBusy(false); }
  };

  const saveDemoAccount = async () => {
    const f = demoForm;
    if (!f.name?.trim() || !f.email?.trim() || (!f.id && !f.password?.trim())) {
      toast.error(fr ? "Nom, email et mot de passe requis" : "Name, email and password required");
      return;
    }
    setDemoBusy(true);
    try {
      const payload = { name: f.name.trim(), email: f.email.trim(), avatar: f.avatar || null, about: f.about || "", social_enabled: !!f.social_enabled };
      if (f.password?.trim()) payload.password = f.password.trim();
      if (f.id) await api.patch(`/admin/demo-accounts/${f.id}`, payload);
      else await api.post("/admin/demo-accounts", payload);
      toast.success(fr ? "Compte enregistré" : "Account saved");
      setDemoForm(null);
      loadDemoAccounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (fr ? "Échec de l'enregistrement" : "Save failed"));
    } finally { setDemoBusy(false); }
  };

  const deleteDemoAccount = async (id) => {
    if (!window.confirm(fr ? "Supprimer ce compte démo et ses publications ?" : "Delete this demo account and its posts?")) return;
    try {
      await api.delete(`/admin/demo-accounts/${id}`);
      toast.success(fr ? "Compte supprimé" : "Account deleted");
      setDemoAccounts((l) => (l || []).filter((x) => x.id !== id));
    } catch { toast.error(fr ? "Échec de la suppression" : "Delete failed"); }
  };

  const uploadDemoAvatar = async (file) => {
    if (!file) return;
    setDemoAvatarUp(1);
    try {
      const { uploadFileDirect } = await import("@/lib/uploads");
      const { url } = await uploadFileDirect(file, (p) => setDemoAvatarUp(Math.max(p, 1)), "avatar");
      setDemoForm((f) => ({ ...f, avatar: url }));
    } catch { toast.error(fr ? "Échec de l'envoi de l'image" : "Image upload failed"); }
    finally { setDemoAvatarUp(0); }
  };

  const runDiag = async () => {
    try {
      const { getBridgeDiagnostics } = await import("@/lib/medianOneSignal");
      const d = await getBridgeDiagnostics();
      setDiag(d);
      // If the bridge returned identifiers, register them server-side so targeting works.
      if (d?.info?.subscription?.id || d?.info?.externalId) {
        api.post("/me/onesignal", {
          app_id: d.info.appId,
          one_signal_id: d.info.oneSignalId,
          external_id: d.info.externalId,
          subscription_id: d.info.subscription?.id,
          opted_in: d.info.subscription?.optedIn,
        }).catch(() => {});
      }
    } catch (e) { setDiag({ error: String(e) }); }
  };

  const sendTestPush = async () => {
    setPushBusy(true);
    try {
      const { data } = await api.post("/admin/push/test", {});
      const native = data.fcm_sent > 0 || data.native_delivered;
      const web = data.web_sent > 0;
      if (native && web) toast.success(t("a_push_sent_both"));
      else if (native) toast.success(t("a_push_sent_native"));
      else if (web) toast.success(t("a_push_sent_web"));
      else if (data.native_known && !data.native_opted_in) toast(t("a_push_no_permission"), { duration: 7000 });
      else toast(t("a_push_none"), { duration: 7000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("s_failed"));
    } finally { setPushBusy(false); }
  };
  const sendTestCallPush = async () => {
    setPushBusy(true);
    try {
      const { data } = await api.post("/admin/push/test-call", {});
      if (data.delivered) toast.success((lang === "fr" ? "Appel test envoyé via " : "Test call sent via ") + data.channel + (lang === "fr" ? ". Fermez l'app pour vérifier la sonnerie." : ". Close the app to check ringing."), { duration: 7000 });
      else toast(((lang === "fr" ? "Échec de l'appel test : " : "Test call failed: ") + (data.errors ? JSON.stringify(data.errors) : (lang === "fr" ? "aucun appareil natif" : "no native device"))), { duration: 9000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Échec" : "Failed"));
    } finally { setPushBusy(false); }
  };
  const downloadLogo = async () => {
    try {
      const res = await api.get("/admin/brand-logo", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "RoyalChat-logo.png";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(t("a_logo_downloaded"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("s_failed")); }
  };
  const clearEmailLogs = async () => {
    try {
      const { data } = await api.post("/admin/email-stats/clear", null, { params: { older_than_hours: 24 } });
      toast.success(t("a_email_cleared").replace("{n}", data.deleted));
      const { data: em } = await api.get("/admin/email-stats");
      setEmailStats(em);
    } catch (e) { toast.error(e?.response?.data?.detail || t("s_failed")); }
  };
  const [welcome, setWelcome] = useState(null); // {enabled, messages:{lang:text}, media...}
  const [welcomeLang, setWelcomeLang] = useState("en");
  const [welcomeBusy, setWelcomeBusy] = useState(false);
  const [cleanup, setCleanup] = useState({ mode: "age", older_than_days: 30, types: [...MEDIA_TYPES], delete_files: true });
  const [sched, setSched] = useState({ enabled: false, days: 30, types: [...MEDIA_TYPES] });
  const [invite, setInvite] = useState("");
  const [respondId, setRespondId] = useState(null);
  const [respondText, setRespondText] = useState("");
  const [pwUserId, setPwUserId] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [bulkMsgOpen, setBulkMsgOpen] = useState(false);
  const [bulkMsgText, setBulkMsgText] = useState("");
  const [mediaStats, setMediaStats] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [mkt, setMkt] = useState(null);
  const [terms, setTerms] = useState(null);
  const [adminShops, setAdminShops] = useState(null);
  const [reports, setReports] = useState(null);
  const [andVers, setAndVers] = useState(null);    // { active_id, versions:[...] } | null
  const [andVersion, setAndVersion] = useState("");
  const [andBusy, setAndBusy] = useState(false);
  const [andProgress, setAndProgress] = useState(0);

  const loadAll = async () => {
    const jobs = {};
    if (has("overview")) jobs.a = api.get("/admin/analytics");
    if (has("overview")) jobs.em = api.get("/admin/email-stats");
    if (has("users")) jobs.u = api.get("/admin/users");
    if (has("feedback")) jobs.s = api.get("/admin/suggestions");
    if (has("cleanup")) { jobs.sc = api.get("/admin/cleanup-schedule"); jobs.iv = api.get("/invite-message"); }
    const keys = Object.keys(jobs);
    const results = await Promise.allSettled(keys.map((k) => jobs[k]));
    const map = {};
    keys.forEach((k, i) => { if (results[i].status === "fulfilled") map[k] = results[i].value.data; });
    if (map.a) setAn(map.a);
    if (map.em) setEmailStats(map.em);
    if (map.u) setUsers(map.u);
    if (map.s) setSugs(map.s);
    if (map.sc) setSched(map.sc);
    if (map.iv) setInvite(map.iv.message);
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (tab === "media" && !mediaStats) api.get("/admin/media-stats").then(({ data }) => { setMediaStats(data); if (data.migration?.running) { setMigrating(true); pollMigration(); } }).catch(() => {});
    if (tab === "market" && !mkt) api.get("/market/settings").then(({ data }) => setMkt(data)).catch(() => {});
    if (tab === "market" && !adminShops) api.get("/admin/shops").then(({ data }) => setAdminShops(data)).catch(() => {});
    if (tab === "terms" && terms === null) api.get("/terms").then(({ data }) => setTerms(data.terms)).catch(() => {});
    if (tab === "reports" && reports === null) api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {});
    if (tab === "broadcast" && welcome === null) api.get("/admin/welcome-message").then(({ data }) => setWelcome(data)).catch(() => {});
    if (tab === "android" && andVers === null) api.get("/admin/android-versions").then(({ data }) => setAndVers(data)).catch(() => setAndVers({ active_id: null, versions: [] }));
    if (tab === "demo" && demoAccounts === null) loadDemoAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadAndVers = () => api.get("/admin/android-versions").then(({ data }) => setAndVers(data)).catch(() => {});

  const uploadApk = async (file) => {
    if (!file) return;
    if (!andVersion.trim()) { toast.error(fr ? "Entrez d'abord le numéro de version" : "Enter the version number first"); return; }
    if (!/\.apk$/i.test(file.name)) { toast.error(fr ? "Veuillez sélectionner un fichier .apk" : "Please select a .apk file"); return; }
    setAndBusy(true); setAndProgress(0);
    try {
      const up = await uploadFileChunked(file, setAndProgress, "android");
      const fileId = up.url.split("/api/files/")[1];
      const { data } = await api.post("/admin/android-app", {
        file_id: fileId, version: andVersion.trim(), size: file.size,
        name: file.name, content_type: "application/vnd.android.package-archive", notify: true,
      });
      toast.success(fr
        ? `APK v${data.app.version} publié ✓ — notification envoyée aux utilisateurs Android`
        : `APK v${data.app.version} published ✓ — update push sent to Android users`);
      setAndVersion("");
      await loadAndVers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Échec du téléversement" : "Upload failed"));
    } finally { setAndBusy(false); setAndProgress(0); }
  };

  const activateVer = async (id) => {
    try { const { data } = await api.post(`/admin/android-versions/${id}/activate`); toast.success(fr ? `v${data.version} est maintenant active ✓` : `v${data.version} is now live ✓`); await loadAndVers(); }
    catch { toast.error(fr ? "Échec du changement de version" : "Failed to switch version"); }
  };

  const deleteVer = async (id) => {
    if (!window.confirm(fr ? "Supprimer cette version définitivement ?" : "Delete this version permanently?")) return;
    try { await api.delete(`/admin/android-versions/${id}`); toast.success(fr ? "Version supprimée" : "Version deleted"); await loadAndVers(); }
    catch { toast.error(fr ? "Échec de la suppression" : "Delete failed"); }
  };

  const deleteModuleMedia = async (mod) => {
    if (!window.confirm(t("a_confirm_del_media"))) return;
    try { const { data } = await api.delete(`/admin/media/${mod}`); toast.success(`${data.deleted} ${t("a_files_deleted")}`); const { data: fresh } = await api.get("/admin/media-stats"); setMediaStats(fresh); }
    catch { toast.error(t("a_failed")); }
  };
  const pollMigration = () => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/media-stats");
        setMediaStats(data);
        if (!data.migration?.running) { clearInterval(iv); setMigrating(false); }
      } catch { clearInterval(iv); setMigrating(false); }
    }, 1500);
  };
  const startMigration = async () => {
    if (!window.confirm(fr ? "Déplacer tous les fichiers MongoDB restants vers IONOS ? La copie MongoDB sera supprimée après vérification." : "Move all remaining MongoDB files to IONOS? The MongoDB copy is deleted after each file is verified.")) return;
    try { setMigrating(true); await api.post("/admin/storage/migrate"); toast.success(fr ? "Migration démarrée" : "Migration started"); pollMigration(); }
    catch (e) { setMigrating(false); toast.error(e?.response?.data?.detail || t("a_failed")); }
  };
  const saveMarket = async () => {
    try { const { data } = await api.put("/admin/market/settings", { currency: mkt.currency, commission_percent: mkt.commission_percent, tax_percent: mkt.tax_percent, boost_options: mkt.boost_options, listing_days: mkt.listing_days, categories: mkt.categories }); setMkt(data); toast.success(t("a_market_saved")); }
    catch { toast.error(t("a_failed")); }
  };
  const saveTerms = async () => {
    try { await api.put("/admin/terms", { terms }); toast.success(t("a_terms_saved")); } catch { toast.error(t("a_failed")); }
  };
  const updBoost = (i, patch) => setMkt((m) => ({ ...m, boost_options: (m.boost_options || []).map((o, idx) => idx === i ? { ...o, ...patch } : o) }));
  const verifyShop = async (id, verified) => {
    try { await api.post(`/admin/shops/${id}/verify`, { verified }); setAdminShops((s) => s.map((x) => x.id === id ? { ...x, verified } : x)); toast.success(verified ? t("a_verified") : t("a_unverified")); }
    catch { toast.error(t("a_failed")); }
  };

  const updateUser = async (id, patch) => { await api.put(`/admin/users/${id}`, patch); loadAll(); toast.success(t("a_updated")); };
  const delUser = async (id) => { if (!window.confirm(t("a_delete_user_q"))) return; await api.delete(`/admin/users/${id}`); loadAll(); };
  const setUserPassword = async (id) => {
    if (pwValue.trim().length < 6) return toast.error(t("a_pw_min"));
    try { await api.put(`/admin/users/${id}`, { password: pwValue }); toast.success(t("a_pw_updated")); setPwUserId(null); setPwValue(""); }
    catch (e) { toast.error(t("a_pw_fail")); }
  };
  const toggleSelect = (id) => setSelectedIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  const exitSelect = () => { setSelectMode(false); setSelectedIds([]); };
  const bulkAction = async (action) => {
    if (!selectedIds.length) return;
    try { const { data } = await api.post("/admin/users/bulk", { ids: selectedIds, action }); toast.success(t("a_updated")); exitSelect(); loadAll(); return data; }
    catch (e) { toast.error(t("a_failed")); }
  };
  const sendBulkMessage = async () => {
    const text = bulkMsgText.trim();
    if (!text || !selectedIds.length) return;
    try {
      const { data } = await api.post("/admin/users/bulk-message", { ids: selectedIds, text });
      toast.success(t("a_msg_sent_n").replace("{n}", data.sent));
      setBulkMsgText(""); setBulkMsgOpen(false); exitSelect();
    } catch (e) { toast.error(t("a_failed")); }
  };
  useEffect(() => {
    if (!selectedUserId) { setUserStats(null); return; }
    setUserStats(null);
    api.get(`/admin/users/${selectedUserId}/stats`).then(({ data }) => setUserStats(data)).catch(() => {});
  }, [selectedUserId]);

  const attachBroadcast = async (e, type) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const { url, name } = await uploadFileChunked(f); setBAtt({ url, type, name }); }
    catch (err) { toast.error(t("a_upload_failed")); } finally { setBusy(false); e.target.value = ""; }
  };
  const sendBroadcast = async () => {
    if (!bText.trim() && !bAtt) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", { text: bText || null, media: bAtt?.url, media_type: bAtt?.type, media_name: bAtt?.name });
      toast.success(t("a_sent_broadcast")); setBText(""); setBAtt(null);
    } finally { setBusy(false); }
  };

  const respond = async (id) => { await api.post(`/admin/suggestions/${id}/respond`, { text: respondText }); setRespondId(null); setRespondText(""); loadAll(); toast.success(t("a_reply_sent")); };
  const delSug = async (id) => { await api.delete(`/admin/suggestions/${id}`); loadAll(); };

  const reloadReports = () => api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {});
  const saveWelcome = async () => {
    try { setWelcomeBusy(true); const { data } = await api.put("/admin/welcome-message", { enabled: welcome.enabled, messages: welcome.messages || {}, media: welcome.media || null, media_type: welcome.media_type || null, media_name: welcome.media_name || null }); setWelcome(data); toast.success(t("a_welcome_saved")); }
    catch { toast.error(t("a_failed")); } finally { setWelcomeBusy(false); }
  };
  const attachWelcome = async (e, type) => {
    const f = e.target.files?.[0]; if (!f) return;
    setWelcomeBusy(true);
    try { const { url, name } = await uploadFileChunked(f); setWelcome((w) => ({ ...w, media: url, media_type: type, media_name: name })); }
    catch (err) { toast.error(t("a_upload_failed")); } finally { setWelcomeBusy(false); e.target.value = ""; }
  };
  const reportAction = async (id, action) => {
    if (action === "suspend" && !window.confirm(t("a_confirm_suspend"))) return;
    try { await api.post(`/admin/reports/${id}/${action}`); await reloadReports(); loadAll();
      toast.success(t(action === "warn" ? "a_warn_sent" : action === "suspend" ? "a_suspended_ok" : action === "unsuspend" ? "a_unsuspended_ok" : "a_dismissed_ok")); }
    catch { toast.error(t("a_failed")); }
  };
  const delReport = async (id) => { try { await api.delete(`/admin/reports/${id}`); await reloadReports(); loadAll(); } catch { toast.error(t("a_failed")); } };

  const runCleanup = async () => {
    if (cleanup.types.length === 0) return toast.error(t("a_pick_category"));
    if (!window.confirm(t("a_confirm_cleanup"))) return;
    setBusy(true);
    try {
      const payload = { types: cleanup.types, delete_files: cleanup.delete_files, older_than_days: cleanup.mode === "all" ? null : cleanup.older_than_days };
      const { data } = await api.post("/admin/cleanup", payload);
      toast.success(`${t("a_removed")}: ${data.deleted_messages} ${t("a_messages").toLowerCase()}, ${data.deleted_files} ${t("a_files")}`); loadAll();
    }
    finally { setBusy(false); }
  };
  const saveSchedule = async () => { await api.put("/admin/cleanup-schedule", sched); toast.success(t("a_schedule_saved")); };
  const saveInvite = async () => { await api.put("/admin/invite-message", { message: invite }); toast.success(t("a_invite_saved")); };

  const toggleType = (arr, setArr, tt) => setArr((s) => ({ ...s, types: s.types.includes(tt) ? s.types.filter((x) => x !== tt) : [...s.types, tt] }));
  const typeLabel = (tt) => ({ image: t("a_images"), video: t("a_videos"), audio: t("a_audio"), document: t("a_documents") }[tt] || tt);

  const ALL_TABS = [["overview", t("a_overview"), "overview"], ["users", t("a_users"), "users"], ["demo", fr ? "Comptes démo" : "Demo Accounts", "demo"], ["broadcast", t("a_broadcast"), "broadcast"], ["suggestions", t("a_feedback"), "feedback"], ["reports", t("a_reports"), "reports"], ["media", t("a_media"), "media"], ["market", t("a_market"), "market"], ["terms", t("a_terms"), "terms"], ["android", lang === "fr" ? "App Android" : "Android App", "android"], ["ringtones", lang === "fr" ? "Sonneries" : "Ringtones", "ringtones"], ["cleanup", t("a_cleanup"), "cleanup"]];
  const filteredUsers = users.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.phone || "").includes(q);
  });
  const selectedUser = users.find((u) => u.id === selectedUserId) || null;
  const TABS = ALL_TABS.filter(([, , sec]) => has(sec));
  const Stat = ({ icon: Icon, label, value }) => (
    <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4"><Icon size={18} className="text-[var(--rc-gold)] mb-2" /><div className="text-2xl font-heading">{value ?? "—"}</div><div className="text-xs text-[var(--rc-muted)]">{label}</div></div>
  );

  return (
    <Modal title={t("a_dashboard")} onClose={onClose}>
      <div className="flex gap-1.5 mb-4 overflow-x-auto no-scrollbar">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === k ? "bg-[var(--rc-gold)] text-black" : "bg-[var(--rc-bg)] text-[var(--rc-muted)]"}`}>{l}
            {k === "suggestions" && an?.totals?.suggestions_open > 0 && <span className="ml-1.5 text-[10px] bg-red-500 text-white rounded-full px-1.5">{an.totals.suggestions_open}</span>}
            {k === "reports" && an?.totals?.reports_open > 0 && <span className="ml-1.5 text-[10px] bg-red-500 text-white rounded-full px-1.5">{an.totals.reports_open}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && an && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat icon={Users} label={t("a_users")} value={an.totals.users} />
            <Stat icon={MessageSquare} label={t("a_messages")} value={an.totals.messages} />
            <Stat icon={Wifi} label={t("a_online")} value={an.totals.online} />
            <Stat icon={Lightbulb} label={t("a_open_feedback")} value={an.totals.suggestions_open} />
          </div>
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4"><Bars data={an.messages_series} label={t("a_msgs_day")} /></div>
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4"><Bars data={an.users_series} label={t("a_newusers_day")} /></div>
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
            <p className="text-xs text-[var(--rc-muted)] mb-3 flex items-center gap-1.5"><HardDrive size={14} /> {t("a_storage")}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>{t("a_database")}: <span className="text-[var(--rc-gold)]">{fmtBytes(an.storage.data_bytes)}</span></div>
              <div>{t("a_media_files")}: <span className="text-[var(--rc-gold)]">{fmtBytes(an.storage.media_bytes)}</span> ({an.storage.media_count})</div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.entries(an.by_type).map(([tt, n]) => <span key={tt} className="text-xs rounded-full bg-[var(--rc-surface-2)] px-2.5 py-1">{tt}: {n}</span>)}
            </div>
          </div>
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
            <p className="text-xs text-[var(--rc-muted)] mb-3 flex items-center gap-1.5"><Bell size={14} /> {t("a_push_test_title")}</p>
            <p className="text-xs text-[var(--rc-muted)] mb-3">{t("a_push_test_desc")}</p>
            <button onClick={sendTestPush} disabled={pushBusy} data-testid="admin-test-push-btn"
              className="w-full rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-2.5 flex items-center justify-center gap-2 disabled:opacity-50">
              {pushBusy ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />} {t("a_push_send_test")}
            </button>
            <button onClick={runDiag} data-testid="admin-push-diag-btn"
              className="w-full mt-2 rounded-xl border border-[var(--rc-border)] text-[var(--rc-muted)] py-2 text-xs font-medium">
              {t("a_push_diag")}
            </button>
            <button onClick={sendTestCallPush} disabled={pushBusy} data-testid="admin-test-call-push-btn"
              className="w-full mt-2 rounded-xl border border-[var(--rc-gold)]/50 text-[var(--rc-gold)] py-2 text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              <PhoneCall size={14} /> {lang === "fr" ? "Tester la notification d'appel" : "Test call notification"}
            </button>
            {diag && (
              <div className="mt-2 rounded-xl bg-black/40 border border-[var(--rc-border)] p-2.5 text-[10px] font-mono text-[var(--rc-muted)] break-all" data-testid="admin-push-diag-out">
                <div>median bridge: <b className={diag.hasMedian ? "text-emerald-400" : "text-red-400"}>{String(!!diag.hasMedian)}</b></div>
                <div>onesignal plugin: <b className={diag.hasOneSignal ? "text-emerald-400" : "text-red-400"}>{String(!!diag.hasOneSignal)}</b></div>
                <div>methods: {(diag.methods || []).join(", ") || "—"}</div>
                <div className="mt-1">subscriptionId: <b className="text-[var(--rc-text)]">{diag.info?.subscription?.id || "—"}</b></div>
                <div>optedIn: <b className="text-[var(--rc-text)]">{String(diag.info?.subscription?.optedIn)}</b></div>
                <div>externalId: <b className="text-[var(--rc-text)]">{diag.info?.externalId || "—"}</b></div>
                <div>oneSignalId: <b className="text-[var(--rc-text)]">{diag.info?.oneSignalId || diag.info?.oneSignalUserId || "—"}</b></div>
                {diag.info && <div className="mt-1 opacity-70">raw: {JSON.stringify(diag.info).slice(0, 400)}</div>}
              </div>
            )}
          </div>
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
            <p className="text-xs text-[var(--rc-muted)] mb-3 flex items-center gap-1.5"><ImageIcon size={14} /> {t("a_brand_logo")}</p>
            <div className="flex items-center gap-4">
              <img src={`${BACKEND}/api/design/logo`} alt="RoyalChat Nova logo" data-testid="admin-logo-preview"
                className="h-16 w-16 rounded-2xl object-cover border border-[var(--rc-border)]" />
              <button onClick={downloadLogo} data-testid="admin-download-logo-btn"
                className="flex-1 rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-2.5 flex items-center justify-center gap-2">
                <Download size={16} /> {t("a_download_logo")}
              </button>
            </div>
          </div>

          {emailStats && (
            <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4" data-testid="admin-email-stats">
              <p className="text-xs text-[var(--rc-muted)] mb-3 flex items-center gap-1.5"><Mail size={14} /> {t("a_email_delivery")}
                <button onClick={clearEmailLogs} data-testid="admin-email-clear-btn"
                  className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--rc-muted)] hover:text-red-400 transition-colors">
                  <Trash2 size={12} /> {t("a_email_clear")}
                </button>
                <span className="uppercase text-[10px] tracking-wider text-[var(--rc-gold)]">{emailStats.provider}</span>
              </p>
              <div className="flex items-center gap-4 mb-4">
                <div className={`text-3xl font-bold ${emailStats.delivery_rate >= 95 ? "text-emerald-400" : emailStats.delivery_rate >= 80 ? "text-amber-400" : "text-red-400"}`} data-testid="admin-email-rate">
                  {emailStats.delivery_rate}%
                </div>
                <div className="text-xs text-[var(--rc-muted)] leading-relaxed">
                  {t("a_email_rate_label")}<br />
                  <span className="text-[var(--rc-text)]">{emailStats.delivered}</span> {t("a_email_delivered")} · <span className="text-red-400">{emailStats.failed}</span> {t("a_email_failed")} · {emailStats.total} {t("a_email_total")}
                </div>
              </div>
              {emailStats.failed_24h > 0 && (
                <div className="mb-3 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-300" data-testid="admin-email-warning">
                  ⚠️ {emailStats.failed_24h} {t("a_email_failed_24h")} — {t("a_email_throttle_hint")}
                </div>
              )}
              {emailStats.by_day && emailStats.by_day.length > 0 && (
                <div className="flex items-end gap-1 h-16 mb-3">
                  {emailStats.by_day.map((d) => {
                    const tot = d.delivered + d.failed;
                    const max = Math.max(...emailStats.by_day.map((x) => x.delivered + x.failed), 1);
                    const h = Math.max(6, Math.round((tot / max) * 56));
                    const failFrac = tot ? d.failed / tot : 0;
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.delivered} ok, ${d.failed} failed`}>
                        <div className="w-full rounded-t overflow-hidden flex flex-col justify-end" style={{ height: h }}>
                          <div className="bg-red-400" style={{ height: `${failFrac * 100}%` }} />
                          <div className="bg-[var(--rc-gold)]" style={{ height: `${(1 - failFrac) * 100}%` }} />
                        </div>
                        <span className="text-[9px] text-[var(--rc-muted)]">{d.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {emailStats.recent_failures && emailStats.recent_failures.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[var(--rc-muted)] mb-1.5">{t("a_email_recent_failures")}</p>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {emailStats.recent_failures.map((f, i) => (
                      <div key={i} className="text-xs rounded-lg bg-[var(--rc-surface)] px-2.5 py-1.5" data-testid={`admin-email-failure-${i}`}>
                        <div className="flex justify-between gap-2"><span className="text-[var(--rc-text)] truncate">{f.to}</span><span className="text-[var(--rc-muted)] shrink-0">{(f.created_at || "").slice(5, 16).replace("T", " ")}</span></div>
                        <div className="text-red-400/90 truncate">{f.error || "unknown error"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-3 pb-20">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rc-muted)]" />
              <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder={t("a_search_users")} data-testid="admin-user-search"
                className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
            </div>
            <button onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} data-testid="admin-select-toggle"
              className={`shrink-0 rounded-xl px-3 py-2.5 text-sm font-medium border ${selectMode ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>
              {selectMode ? t("a_cancel") : t("a_select")}
            </button>
          </div>
          <div className="text-xs text-[var(--rc-muted)]">{filteredUsers.length} {t("a_users").toLowerCase()}</div>
          <div className="space-y-1.5">
            {filteredUsers.map((u) => {
              const checked = selectedIds.includes(u.id);
              return (
              <button key={u.id} onClick={() => (selectMode ? toggleSelect(u.id) : setSelectedUserId(u.id))} data-testid={`admin-user-${u.id}`}
                className={`w-full flex items-center gap-3 rounded-2xl bg-[var(--rc-bg)] border p-3 text-left transition-colors ${checked ? "border-[var(--rc-gold)]" : "border-[var(--rc-border)] hover:border-[var(--rc-gold)]/40"}`}>
                {selectMode && (
                  <span className={`shrink-0 h-5 w-5 rounded-md border grid place-items-center ${checked ? "bg-[var(--rc-gold)] border-[var(--rc-gold)]" : "border-[var(--rc-border)]"}`} data-testid={`admin-select-${u.id}`}>
                    {checked && <Check size={13} className="text-black" />}
                  </span>
                )}
                <div className="relative shrink-0">
                  <img src={avatarUrl(u.id)} alt="" loading="lazy" className="h-11 w-11 rounded-full object-cover border border-[var(--rc-gold)]/40 bg-[var(--rc-surface)]" />
                  {u.online && <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-[var(--rc-bg)]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                    {u.name}
                    {u.role === "admin" && <ShieldCheck size={13} className="text-[var(--rc-gold)] shrink-0" />}
                    {u.status === "suspended" && <span className="text-[10px] rounded bg-red-500/15 text-red-400 px-1.5 py-0.5 shrink-0">{t("a_suspended")}</span>}
                  </div>
                  <div className="text-xs text-[var(--rc-muted)] truncate flex items-center gap-1.5">
                    <span className="truncate">{u.email}</span>
                    {(u.platforms || []).map((os) => (
                      <span key={os} data-testid={`admin-user-os-${os}`}
                        className="shrink-0 inline-flex items-center gap-0.5 rounded bg-[var(--rc-surface)] border border-[var(--rc-border)] px-1.5 py-0.5 text-[10px] text-[var(--rc-gold)]">
                        <Smartphone size={10} /> {os}
                      </span>
                    ))}
                  </div>
                </div>
                {!selectMode && u.role && u.role !== "user" && <span className="text-[10px] rounded-lg bg-[var(--rc-surface)] border border-[var(--rc-border)] px-2 py-1 capitalize shrink-0">{u.role}</span>}
              </button>
              );
            })}
            {filteredUsers.length === 0 && <div className="text-center text-sm text-[var(--rc-muted)] py-10">{t("a_no_users")}</div>}
          </div>

          {selectMode && selectedIds.length > 0 && (
            <div className="fixed left-0 right-0 bottom-0 z-[65] bg-[var(--rc-surface)] border-t border-[var(--rc-border)] p-3 flex items-center gap-2" data-testid="admin-bulk-bar">
              <span className="text-sm text-[var(--rc-muted)] mr-auto">{selectedIds.length} {t("a_selected")}</span>
              <button onClick={() => setBulkMsgOpen(true)} data-testid="admin-bulk-message" className="flex items-center gap-1.5 rounded-xl border border-[var(--rc-gold)]/40 text-[var(--rc-gold)] px-3 py-2 text-sm font-medium"><Send size={15} /> {t("a_message_action")}</button>
              <button onClick={() => bulkAction("suspend")} data-testid="admin-bulk-suspend" className="flex items-center gap-1.5 rounded-xl border border-amber-500/30 text-amber-300 bg-amber-500/10 px-3 py-2 text-sm font-medium"><Ban size={15} /> {t("a_suspend")}</button>
              <button onClick={() => bulkAction("activate")} data-testid="admin-bulk-activate" className="flex items-center gap-1.5 rounded-xl border border-emerald-500/30 text-emerald-300 bg-emerald-500/10 px-3 py-2 text-sm font-medium"><ShieldCheck size={15} /> {t("a_activate")}</button>
            </div>
          )}
        </div>
      )}

      {tab === "demo" && (
        <div className="space-y-3" data-testid="admin-demo">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--rc-muted)]">{fr ? "Comptes de démonstration créés par l'admin." : "Demo accounts created by admins."}</p>
            <button onClick={() => setDemoForm({ name: "", email: "", password: "", avatar: "", about: "", social_enabled: true })}
              data-testid="demo-create-btn" className="flex items-center gap-1.5 rounded-xl bg-[var(--rc-gold)] text-black font-semibold px-3 py-2 text-sm active:scale-95 transition-transform">
              <UserPlus size={16} /> {fr ? "Créer" : "Create"}
            </button>
          </div>

          {demoAccounts === null ? (
            <div className="text-center py-10 text-[var(--rc-muted)]"><Loader2 className="inline animate-spin" size={18} /></div>
          ) : demoAccounts.length === 0 ? (
            <div className="text-center py-10 text-[var(--rc-muted)]" data-testid="demo-empty">{fr ? "Aucun compte démo pour l'instant." : "No demo accounts yet."}</div>
          ) : (
            <div className="space-y-2" data-testid="demo-list">
              {demoAccounts.map((a) => (
                <div key={a.id} data-testid={`demo-row-${a.id}`} className="flex items-center gap-3 rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-3">
                  <span className="h-11 w-11 rounded-xl overflow-hidden border border-[var(--rc-border)] bg-[var(--rc-surface)] grid place-items-center text-[var(--rc-gold)] font-bold shrink-0">
                    {a.avatar ? <img src={a.avatar} alt="" className="h-full w-full object-cover" /> : (a.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate flex items-center gap-2">{a.name}
                      {a.social_enabled && <span className="rounded-full bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] text-[10px] font-bold px-2 py-0.5">SOCIAL</span>}
                    </div>
                    <div className="text-xs text-[var(--rc-muted)] truncate">{a.email}</div>
                  </div>
                  <button onClick={() => { setDemoPostFor(a); setDemoPostText(""); setDemoPostImg(""); }}
                    data-testid={`demo-post-${a.id}`} className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-gold)] hover:bg-[var(--rc-surface)]" title={fr ? "Publier" : "Post"}><PenSquare size={17} /></button>
                  <button onClick={() => setDemoForm({ id: a.id, name: a.name || "", email: a.email || "", password: "", avatar: a.avatar || "", about: a.about || "", social_enabled: !!a.social_enabled })}
                    data-testid={`demo-edit-${a.id}`} className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-text)] hover:bg-[var(--rc-surface)]"><SlidersHorizontal size={17} /></button>
                  <button onClick={() => deleteDemoAccount(a.id)} data-testid={`demo-delete-${a.id}`} className="h-9 w-9 grid place-items-center rounded-full text-red-400 hover:bg-red-500/10"><Trash2 size={17} /></button>
                </div>
              ))}
            </div>
          )}

          {demoForm && (
            <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => !demoBusy && setDemoForm(null)} data-testid="demo-form-sheet">
              <div className="w-full sm:max-w-md bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-t-3xl sm:rounded-3xl p-4 max-h-[92vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-xl">{demoForm.id ? (fr ? "Modifier le compte" : "Edit account") : (fr ? "Nouveau compte démo" : "New demo account")}</h3>
                  <button onClick={() => setDemoForm(null)} data-testid="demo-form-close" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-surface)]"><X size={18} /></button>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="h-16 w-16 rounded-2xl overflow-hidden border border-[var(--rc-border)] bg-[var(--rc-surface)] grid place-items-center text-[var(--rc-gold)] text-xl font-bold shrink-0">
                    {demoForm.avatar ? <img src={demoForm.avatar} alt="" className="h-full w-full object-cover" /> : (demoForm.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 rounded-xl border border-[var(--rc-border)] px-3 py-2 text-sm cursor-pointer hover:bg-[var(--rc-surface)]" data-testid="demo-avatar-upload-label">
                      <UploadCloud size={16} /> {demoAvatarUp > 0 ? `${demoAvatarUp}%` : (fr ? "Téléverser un avatar" : "Upload avatar")}
                      <input type="file" accept="image/*" className="hidden" data-testid="demo-avatar-input" onChange={(e) => uploadDemoAvatar(e.target.files?.[0])} />
                    </label>
                  </div>
                </div>
                <div className="space-y-2.5">
                  <input value={demoForm.name} onChange={(e) => setDemoForm((f) => ({ ...f, name: e.target.value }))} placeholder={fr ? "Nom" : "Name"} data-testid="demo-input-name"
                    className="w-full rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--rc-gold)]" />
                  <input value={demoForm.email} onChange={(e) => setDemoForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" type="email" data-testid="demo-input-email"
                    className="w-full rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--rc-gold)]" />
                  <input value={demoForm.password} onChange={(e) => setDemoForm((f) => ({ ...f, password: e.target.value }))} placeholder={demoForm.id ? (fr ? "Nouveau mot de passe (laisser vide)" : "New password (leave blank)") : (fr ? "Mot de passe" : "Password")} data-testid="demo-input-password"
                    className="w-full rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--rc-gold)]" />
                  <input value={demoForm.avatar} onChange={(e) => setDemoForm((f) => ({ ...f, avatar: e.target.value }))} placeholder={fr ? "URL de l'avatar (optionnel)" : "Avatar URL (optional)"} data-testid="demo-input-avatar"
                    className="w-full rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--rc-gold)]" />
                  <textarea value={demoForm.about} onChange={(e) => setDemoForm((f) => ({ ...f, about: e.target.value }))} placeholder={fr ? "Bio (optionnel)" : "Bio (optional)"} rows={2} data-testid="demo-input-about"
                    className="w-full rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--rc-gold)] resize-none" />
                  <label className="flex items-center justify-between rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2.5 text-sm cursor-pointer">
                    <span>{fr ? "Activer l'onglet Social" : "Enable Social tab"}</span>
                    <input type="checkbox" checked={!!demoForm.social_enabled} onChange={(e) => setDemoForm((f) => ({ ...f, social_enabled: e.target.checked }))} data-testid="demo-input-social" className="h-5 w-5 accent-[var(--rc-gold)]" />
                  </label>
                </div>
                <button onClick={saveDemoAccount} disabled={demoBusy || demoAvatarUp > 0} data-testid="demo-form-save"
                  className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3 active:scale-95 transition-transform disabled:opacity-60">
                  {demoBusy ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} {fr ? "Enregistrer" : "Save"}
                </button>
              </div>
            </div>
          )}

          {demoPostFor && (
            <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => !demoPostBusy && setDemoPostFor(null)} data-testid="demo-post-sheet">
              <div className="w-full sm:max-w-md bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-t-3xl sm:rounded-3xl p-4 max-h-[92vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="h-9 w-9 rounded-xl overflow-hidden border border-[var(--rc-border)] bg-[var(--rc-surface)] grid place-items-center text-[var(--rc-gold)] font-bold shrink-0">
                      {demoPostFor.avatar ? <img src={demoPostFor.avatar} alt="" className="h-full w-full object-cover" /> : (demoPostFor.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs text-[var(--rc-muted)]">{fr ? "Publier en tant que" : "Post as"}</div>
                      <div className="font-medium truncate text-sm">{demoPostFor.name}</div>
                    </div>
                  </div>
                  <button onClick={() => setDemoPostFor(null)} data-testid="demo-post-close" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-surface)]"><X size={18} /></button>
                </div>
                <textarea value={demoPostText} onChange={(e) => setDemoPostText(e.target.value)} rows={4} data-testid="demo-post-text"
                  placeholder={fr ? "Quoi de neuf ?" : "What's on your mind?"}
                  className="w-full rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2.5 text-sm outline-none focus:border-[var(--rc-gold)] resize-none" />
                {demoPostImg && (
                  <div className="relative mt-2">
                    <img src={demoPostImg} alt="" className="w-full max-h-60 object-cover rounded-xl border border-[var(--rc-border)]" />
                    <button onClick={() => setDemoPostImg("")} data-testid="demo-post-img-remove" className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white"><X size={16} /></button>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <label className="inline-flex items-center gap-2 rounded-xl border border-[var(--rc-border)] px-3 py-2 text-sm cursor-pointer hover:bg-[var(--rc-surface)]">
                    <ImageIcon size={16} /> {demoPostImgUp > 0 ? `${demoPostImgUp}%` : (fr ? "Image" : "Image")}
                    <input type="file" accept="image/*" className="hidden" data-testid="demo-post-img-input" onChange={(e) => uploadDemoPostImg(e.target.files?.[0])} />
                  </label>
                  <input value={demoPostImg} onChange={(e) => setDemoPostImg(e.target.value)} placeholder={fr ? "ou URL d'image" : "or image URL"} data-testid="demo-post-img-url"
                    className="flex-1 min-w-0 rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--rc-gold)]" />
                </div>
                <button onClick={submitDemoPost} disabled={demoPostBusy || demoPostImgUp > 0} data-testid="demo-post-submit"
                  className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3 active:scale-95 transition-transform disabled:opacity-60">
                  {demoPostBusy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} {fr ? "Publier" : "Post"}
                </button>
              </div>
            </div>
          )}

        </div>
      )}


      {tab === "broadcast" && (
        <div>
          <p className="text-sm text-[var(--rc-muted)] mb-3">{t("a_broadcast_desc_1")} <span className="text-[var(--rc-gold)]">RoyalChat Nova</span> {t("a_broadcast_desc_2")}</p>
          <textarea value={bText} onChange={(e) => setBText(e.target.value)} rows={4} placeholder={t("a_announcement_ph")} data-testid="broadcast-input"
            className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-2xl px-4 py-3 focus:outline-none focus:border-[var(--rc-gold)]/50" />
          {bAtt && <div className="mt-2 text-xs text-[var(--rc-gold)] flex items-center gap-2">📎 {bAtt.name} <button onClick={() => setBAtt(null)} className="underline">{t("a_remove")}</button></div>}
          <div className="flex items-center gap-2 mt-3">
            <label className="flex items-center gap-1.5 rounded-full border border-[var(--rc-border)] px-3 py-2 text-sm cursor-pointer"><ImageIcon size={15} /> {t("a_image")}<input type="file" accept="image/*" hidden onChange={(e) => attachBroadcast(e, "image")} /></label>
            <label className="flex items-center gap-1.5 rounded-full border border-[var(--rc-border)] px-3 py-2 text-sm cursor-pointer"><Paperclip size={15} /> {t("a_file")}<input type="file" hidden onChange={(e) => attachBroadcast(e, e.target.files[0]?.type.startsWith("video") ? "video" : "document")} /></label>
            <button onClick={sendBroadcast} disabled={busy} className="ml-auto flex items-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold px-6 py-2.5" data-testid="send-broadcast-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {t("a_broadcast")}</button>
          </div>

          {/* Automatic welcome message for new users */}
          <div className="mt-6 pt-5 border-t border-[var(--rc-border)]" data-testid="welcome-editor">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 font-medium"><Sparkles size={16} className="text-[var(--rc-gold)]" /> {t("a_welcome_title")}</div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <span className="text-[var(--rc-muted)]">{welcome?.enabled ? t("a_on") : t("a_off")}</span>
                <input type="checkbox" checked={!!welcome?.enabled} disabled={!welcome} onChange={(e) => setWelcome({ ...welcome, enabled: e.target.checked })} data-testid="welcome-enabled-toggle" className="h-4 w-4 accent-[var(--rc-gold)]" />
              </label>
            </div>
            <p className="text-xs text-[var(--rc-muted)] mb-3">{t("a_welcome_desc")}</p>
            {welcome === null ? (
              <p className="text-sm text-[var(--rc-muted)] py-4">{t("a_loading")}</p>
            ) : (
              <>
                <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="welcome-lang-tabs">
                  {LANGS.map((l) => {
                    const filled = !!welcome.messages?.[l.code]?.trim();
                    return (
                      <button key={l.code} onClick={() => setWelcomeLang(l.code)} data-testid={`welcome-lang-${l.code}`}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${welcomeLang === l.code ? "bg-[var(--rc-gold)] text-black border-[var(--rc-gold)]" : "border-[var(--rc-border)] text-[var(--rc-muted)] hover:text-[var(--rc-text)]"}`}>
                        {l.flag} {l.label}{filled ? "" : " ·"}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[var(--rc-muted)] mb-2">{t("a_welcome_lang_hint")}</p>
                <textarea value={welcome.messages?.[welcomeLang] || ""} dir={welcomeLang === "ar" ? "rtl" : "ltr"}
                  onChange={(e) => setWelcome({ ...welcome, messages: { ...(welcome.messages || {}), [welcomeLang]: e.target.value } })} rows={9} data-testid="welcome-text"
                  className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50 whitespace-pre-wrap" />
                {welcome.media && (
                  <div className="mt-3 relative rounded-2xl overflow-hidden border border-[var(--rc-border)] bg-[var(--rc-bg)]" data-testid="welcome-media-preview">
                    {welcome.media_type === "image" ? (
                      <img src={welcome.media} alt="" className="w-full max-h-52 object-cover" />
                    ) : welcome.media_type === "video" ? (
                      <video src={welcome.media} controls className="w-full max-h-52 bg-black" />
                    ) : (
                      <div className="flex items-center gap-2 px-4 py-3 text-sm"><Paperclip size={15} className="text-[var(--rc-gold)]" /> {welcome.media_name || t("attach_document")}</div>
                    )}
                    <button onClick={() => setWelcome({ ...welcome, media: null, media_type: null, media_name: null })} data-testid="welcome-media-remove"
                      className="absolute top-2 right-2 rounded-full bg-black/70 text-white p-1.5 hover:bg-black"><X size={14} /></button>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <label className="flex items-center gap-1.5 rounded-full border border-[var(--rc-border)] px-3 py-2 text-sm cursor-pointer" data-testid="welcome-attach-image"><ImageIcon size={15} /> {t("a_image")}<input type="file" accept="image/*" hidden onChange={(e) => attachWelcome(e, "image")} /></label>
                  <label className="flex items-center gap-1.5 rounded-full border border-[var(--rc-border)] px-3 py-2 text-sm cursor-pointer" data-testid="welcome-attach-file"><Paperclip size={15} /> {t("a_file")}<input type="file" hidden onChange={(e) => attachWelcome(e, e.target.files[0]?.type.startsWith("video") ? "video" : "document")} /></label>
                </div>
                <div className="flex items-center mt-3">
                  <span className="text-[11px] text-[var(--rc-muted)]">{t("a_welcome_from")} <span className="text-[var(--rc-gold)]">RoyalChat Nova</span></span>
                  <button onClick={saveWelcome} disabled={welcomeBusy || (!Object.values(welcome.messages || {}).some((v) => v?.trim()) && !welcome.media)} data-testid="welcome-save-btn"
                    className="ml-auto flex items-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold px-6 py-2.5 disabled:opacity-40">{welcomeBusy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {t("a_save")}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "suggestions" && (
        <div className="space-y-3">
          {sugs.length === 0 && <p className="text-center text-sm text-[var(--rc-muted)] py-10">{t("a_no_feedback")}</p>}
          {sugs.map((s) => (
            <div key={s.id} className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4" data-testid={`suggestion-${s.id}`}>
              <div className="flex items-center gap-2 mb-2"><span className="font-medium text-sm">{s.user_name}</span><span className="text-xs text-[var(--rc-muted)]">{s.user_email}</span>{s.status === "answered" && <span className="ml-auto text-[10px] text-emerald-400">{t("a_answered")}</span>}</div>
              <p className="text-sm whitespace-pre-wrap mb-2">{s.text}</p>
              {s.attachments?.length > 0 && (
                <div className="flex gap-2 mb-2 flex-wrap">
                  {s.attachments.map((a, i) => <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer" data-testid={`sug-att-${s.id}-${i}`}><img src={fileUrl(a.url)} alt="" className="h-16 w-16 rounded-lg object-cover border border-[var(--rc-border)]" /></a>)}
                </div>
              )}
              {respondId === s.id ? (
                <div className="flex gap-2">
                  <input value={respondText} onChange={(e) => setRespondText(e.target.value)} placeholder={t("a_reply_ph")} className="flex-1 bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2 text-sm" data-testid={`respond-input-${s.id}`} />
                  <button onClick={() => respond(s.id)} className="rounded-xl bg-[var(--rc-gold)] text-black px-3 text-sm font-semibold" data-testid={`respond-send-${s.id}`}>{t("a_send")}</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => { setRespondId(s.id); setRespondText(""); }} className="flex items-center gap-1.5 text-sm text-[var(--rc-gold)]" data-testid={`respond-btn-${s.id}`}><Reply size={15} /> {t("a_respond")}</button>
                  <button onClick={() => delSug(s.id)} className="flex items-center gap-1.5 text-sm text-red-400 ml-auto" data-testid={`del-sug-${s.id}`}><Trash2 size={15} /> {t("a_delete")}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "reports" && (
        <div className="space-y-3" data-testid="admin-reports">
          {reports === null && <p className="text-center text-sm text-[var(--rc-muted)] py-10">{t("a_loading") || "…"}</p>}
          {reports !== null && reports.length === 0 && <p className="text-center text-sm text-[var(--rc-muted)] py-10">{t("a_no_reports")}</p>}
          {(reports || []).map((r) => {
            const suspended = r.seller_status === "suspended";
            const isPost = r.kind === "post";
            return (
              <div key={r.id} className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4" data-testid={`report-${r.id}`}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 font-medium">🚩 {isPost ? (fr ? "Publication" : "Post") : (fr ? "Marché" : "Market")}</span>
                  {r.status === "actioned" && r.action === "warned" && <span className="text-[10px] text-amber-400">{t("a_warned")}</span>}
                  {r.action === "removed" && <span className="text-[10px] text-red-400">{fr ? "Publication supprimée" : "Post removed"}</span>}
                  {suspended && <span className="text-[10px] text-red-400 font-semibold">{t("a_seller_suspended")}</span>}
                  {r.status === "dismissed" && <span className="text-[10px] text-[var(--rc-muted)]">{t("a_dismissed")}</span>}
                  <span className="ml-auto text-[10px] text-[var(--rc-muted)]">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="h-9 w-9 rounded-lg overflow-hidden shrink-0 bg-[var(--rc-surface)] grid place-items-center text-xs font-semibold text-[var(--rc-gold)] border border-[var(--rc-border)]">
                    {r.reported_avatar ? <img src={fileUrl(r.reported_avatar)} alt="" className="h-full w-full object-cover" /> : (r.reported_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{r.reported_name}{!r.seller_exists && <span className="text-[10px] text-[var(--rc-muted)]"> ({t("a_user_gone")})</span>}</p>
                    {r.reported_username && <p className="text-[11px] text-[var(--rc-muted)] truncate">@{r.reported_username}</p>}
                  </div>
                </div>
                {isPost ? (
                  <p className="text-xs text-[var(--rc-muted)] mb-1 line-clamp-3 italic">
                    {r.post_deleted ? (fr ? "(publication déjà supprimée)" : "(post already deleted)") : (r.post_excerpt || (r.post_has_media ? (fr ? "[publication média]" : "[media post]") : "—"))}
                  </p>
                ) : (r.product_title && <p className="text-xs text-[var(--rc-muted)] mb-1">{t("a_item")}: {r.product_title}</p>)}
                <p className="text-sm mb-2"><span className="text-[var(--rc-muted)]">{t("a_reason")}: </span>{r.reason || <span className="text-[var(--rc-muted)] italic">{t("a_no_reason")}</span>}</p>
                <p className="text-[11px] text-[var(--rc-muted)] mb-3">{t("a_reported_by")}: {r.reporter_name}</p>
                <div className="flex gap-2 flex-wrap">
                  {isPost && !r.post_deleted && <button onClick={() => reportAction(r.id, "remove")} className="flex items-center gap-1.5 text-xs font-medium rounded-lg px-3 py-1.5 bg-red-500/15 text-red-300 border border-red-500/30" data-testid={`report-remove-${r.id}`}><Trash2 size={13} /> {fr ? "Supprimer la publication" : "Remove post"}</button>}
                  <button onClick={() => reportAction(r.id, "warn")} disabled={!r.seller_exists} className="flex items-center gap-1.5 text-xs font-medium rounded-lg px-3 py-1.5 bg-amber-500/15 text-amber-300 border border-amber-500/30 disabled:opacity-40" data-testid={`report-warn-${r.id}`}><AlertTriangle size={13} /> {t("a_warn")}</button>
                  {suspended ? (
                    <button onClick={() => reportAction(r.id, "unsuspend")} className="flex items-center gap-1.5 text-xs font-medium rounded-lg px-3 py-1.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid={`report-unsuspend-${r.id}`}><ShieldCheck size={13} /> {t("a_unsuspend")}</button>
                  ) : (
                    <button onClick={() => reportAction(r.id, "suspend")} disabled={!r.seller_exists} className="flex items-center gap-1.5 text-xs font-medium rounded-lg px-3 py-1.5 bg-red-500/15 text-red-300 border border-red-500/30 disabled:opacity-40" data-testid={`report-suspend-${r.id}`}><Ban size={13} /> {t("a_suspend")}</button>
                  )}
                  {r.status !== "dismissed" && <button onClick={() => reportAction(r.id, "dismiss")} className="flex items-center gap-1.5 text-xs font-medium rounded-lg px-3 py-1.5 text-[var(--rc-muted)] border border-[var(--rc-border)]" data-testid={`report-dismiss-${r.id}`}><Check size={13} /> {t("a_dismiss")}</button>}
                  <button onClick={() => delReport(r.id)} className="flex items-center gap-1.5 text-xs font-medium text-red-400 ml-auto" data-testid={`report-delete-${r.id}`}><Trash2 size={13} /> {t("a_delete")}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "media" && (
        <div className="space-y-3" data-testid="admin-media">
          <p className="text-sm text-[var(--rc-muted)]">{t("a_media_desc")}</p>
          {!mediaStats ? <div className="text-center py-8 text-[var(--rc-muted)]">{t("a_loading")}</div> : (
            <>
              {/* Object storage summary + migration */}
              <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 space-y-3" data-testid="storage-summary">
                <div className="flex items-center gap-2">
                  <Database size={18} className="text-[var(--rc-gold)]" />
                  <span className="font-medium">{fr ? "Stockage objet (IONOS)" : "Object Storage (IONOS)"}</span>
                  <span className={`ml-auto text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${mediaStats.ionos_enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`} data-testid="ionos-status">
                    {mediaStats.ionos_enabled ? (fr ? "Actif" : "Connected") : (fr ? "Inactif" : "Off")}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-[var(--rc-panel)] border border-[var(--rc-border)] px-3 py-2" data-testid="ionos-total">
                    <div className="text-xs text-[var(--rc-muted)]">IONOS</div>
                    <div className="font-semibold text-[var(--rc-gold)]">{mediaStats.ionos_total} {t("a_files")}</div>
                  </div>
                  <div className="rounded-xl bg-[var(--rc-panel)] border border-[var(--rc-border)] px-3 py-2" data-testid="gridfs-total">
                    <div className="text-xs text-[var(--rc-muted)]">{fr ? "MongoDB (ancien)" : "MongoDB (legacy)"}</div>
                    <div className="font-semibold">{mediaStats.gridfs_total} {t("a_files")}</div>
                  </div>
                </div>
                {mediaStats.migration?.running || migrating ? (
                  <div className="space-y-2" data-testid="migration-progress">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[var(--rc-muted)] flex items-center gap-1.5"><Loader2 size={13} className="animate-spin text-[var(--rc-gold)]" /> {fr ? "Migration en cours…" : "Migrating…"}</span>
                      <span>{mediaStats.migration?.done || 0}/{mediaStats.migration?.total || 0}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[var(--rc-panel)] overflow-hidden">
                      <div className="h-full bg-[var(--rc-gold)] transition-all" style={{ width: `${mediaStats.migration?.total ? Math.round((mediaStats.migration.done / mediaStats.migration.total) * 100) : 0}%` }} />
                    </div>
                  </div>
                ) : (
                  <>
                    {mediaStats.migration && !mediaStats.migration.running && (
                      <div className="text-xs text-[var(--rc-muted)]" data-testid="migration-result">
                        {fr ? "Dernière migration" : "Last migration"}: {mediaStats.migration.migrated || 0} {fr ? "déplacés" : "moved"}{mediaStats.migration.failed ? `, ${mediaStats.migration.failed} ${fr ? "échoués" : "failed"}` : ""}
                      </div>
                    )}
                    <button onClick={startMigration} disabled={!mediaStats.ionos_enabled || !mediaStats.gridfs_total}
                      className="w-full rounded-xl bg-[var(--rc-gold)] text-black font-medium py-2.5 text-sm disabled:opacity-40 flex items-center justify-center gap-2" data-testid="migrate-btn">
                      <UploadCloud size={16} /> {mediaStats.gridfs_total ? (fr ? `Migrer ${mediaStats.gridfs_total} fichiers vers IONOS` : `Migrate ${mediaStats.gridfs_total} files to IONOS`) : (fr ? "Tout est déjà sur IONOS" : "Everything is on IONOS")}
                    </button>
                  </>
                )}
              </div>

              {/* Per-module usage */}
              {[["chat", t("a_chat_media")], ["social", t("a_social_media")], ["marketplace", t("a_market_media")]].map(([mod, label]) => {
                const m = mediaStats.modules[mod];
                return (
                  <div key={mod} className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 flex items-center gap-3" data-testid={`media-${mod}`}>
                    <HardDrive size={20} className="text-[var(--rc-gold)]" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{label}</div>
                      <div className="text-xs text-[var(--rc-muted)]">{m.count} {t("a_files")} · {fmtBytes(m.bytes)}</div>
                      <div className="text-[11px] text-[var(--rc-muted)] mt-0.5">IONOS {m.ionos.count} ({fmtBytes(m.ionos.bytes)}) · Mongo {m.gridfs.count} ({fmtBytes(m.gridfs.bytes)})</div>
                    </div>
                    <button onClick={() => deleteModuleMedia(mod)} disabled={!m.count} className="rounded-xl border border-red-500/30 text-red-400 px-3 py-2 text-sm disabled:opacity-40" data-testid={`media-delete-${mod}`}>{t("a_delete")}</button>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {tab === "market" && (
        <div className="space-y-4" data-testid="admin-market">
          {!mkt ? <div className="text-center py-8 text-[var(--rc-muted)]">{t("a_loading")}</div> : (
            <>
              <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[var(--rc-muted)]">{t("a_commission")}</label>
                    <input type="number" value={mkt.commission_percent ?? 10} onChange={(e) => setMkt({ ...mkt, commission_percent: +e.target.value })} data-testid="market-commission"
                      className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 mt-1" />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--rc-muted)]">{t("a_tax_percent")}</label>
                    <input type="number" value={mkt.tax_percent ?? 0} onChange={(e) => setMkt({ ...mkt, tax_percent: +e.target.value })} data-testid="market-tax"
                      className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 mt-1" />
                  </div>
                </div>
                <p className="text-[11px] text-[var(--rc-muted)] mt-2">{t("a_commission_note")}</p>
                <p className="text-[11px] text-[var(--rc-muted)] mt-1">{t("a_currency_note")}</p>
              </div>

              {/* Payout setup summary (read-only) */}
              {adminShops && (() => {
                const sellShops = adminShops.filter((s) => s.mode === "sell");
                const done = sellShops.filter((s) => s.payout_setup_complete).length;
                const pending = sellShops.length - done;
                return (
                  <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4" data-testid="payout-summary">
                    <p className="text-xs font-semibold tracking-widest text-[var(--rc-muted)] mb-2">{t("a_payout_setup")}</p>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div><div className="text-2xl font-heading">{sellShops.length}</div><div className="text-[11px] text-[var(--rc-muted)]">{t("a_sell_shops")}</div></div>
                      <div><div className="text-2xl font-heading text-emerald-400">{done}</div><div className="text-[11px] text-[var(--rc-muted)]">{t("a_payout_done")}</div></div>
                      <div><div className="text-2xl font-heading text-amber-400">{pending}</div><div className="text-[11px] text-[var(--rc-muted)]">{t("a_payout_pending")}</div></div>
                    </div>
                  </div>
                );
              })()}

              <p className="text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{t("a_listings_boost")}</p>
              <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 space-y-3">
                <div>
                  <label className="text-xs text-[var(--rc-muted)]">{t("a_listing_duration")}</label>
                  <input type="number" value={mkt.listing_days ?? 30} onChange={(e) => setMkt({ ...mkt, listing_days: +e.target.value })} data-testid="market-listing-days"
                    className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-[var(--rc-muted)]">{t("a_boost_opts")}</label>
                  <div className="space-y-2 mt-1">
                    {(mkt.boost_options || []).map((o, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`boost-opt-${i}`}>
                        <div className="flex-1"><span className="text-xs text-[var(--rc-muted)]">{t("a_days")}</span><input type="number" value={o.days} onChange={(e) => updBoost(i, { days: +e.target.value })} data-testid={`boost-days-${i}`} className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2" /></div>
                        <div className="flex-1"><span className="text-xs text-[var(--rc-muted)]">{t("a_price")} ($)</span><input type="number" value={o.price} onChange={(e) => updBoost(i, { price: +e.target.value })} data-testid={`boost-price-${i}`} className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2" /></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{t("a_categories")}</p>
              <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
                <label className="text-xs text-[var(--rc-muted)]">{t("a_cat_desc")}</label>
                <textarea rows={2} value={(mkt.categories || []).join(", ")} onChange={(e) => setMkt({ ...mkt, categories: e.target.value.split(",").map((c) => c.trim()).filter(Boolean) })} data-testid="market-categories"
                  className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 mt-1 text-sm resize-none" />
              </div>

              <button onClick={saveMarket} className="w-full flex items-center justify-center gap-2 rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3" data-testid="market-save"><Save size={16} /> {t("a_save_market")}</button>

              <p className="text-xs font-semibold tracking-widest text-[var(--rc-muted)] pt-2">{t("a_sellers_shops")} ({(adminShops || []).length})</p>
              <div className="space-y-2" data-testid="admin-shops">
                {(adminShops || []).map((s) => (
                  <div key={s.id} className="flex items-center gap-3 rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-3" data-testid={`admin-shop-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate flex items-center gap-1">{s.name} {s.verified && <BadgeCheck size={14} className="text-[var(--rc-gold)]" />}</div>
                      <div className="text-xs text-[var(--rc-muted)] truncate">{s.owner_name} · {s.product_count} {t("a_items")}{s.auto_verified ? ` · ${t("a_subscribed")}` : ""}</div>
                    </div>
                    <button onClick={() => verifyShop(s.id, !s.verified)} disabled={s.auto_verified} data-testid={`verify-shop-${s.id}`}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold ${s.verified ? "border border-[var(--rc-border)] text-[var(--rc-muted)]" : "bg-[var(--rc-gold)] text-black"} ${s.auto_verified ? "opacity-40" : ""}`}>
                      {s.verified ? t("a_unverify") : t("a_verify")}
                    </button>
                  </div>
                ))}
                {adminShops && adminShops.length === 0 && <div className="text-sm text-[var(--rc-muted)] text-center py-4">{t("a_no_shops")}</div>}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "terms" && (
        <div className="space-y-3" data-testid="admin-terms">
          <p className="text-sm text-[var(--rc-muted)]">{t("a_terms_desc")}</p>
          <textarea rows={16} value={terms || ""} onChange={(e) => setTerms(e.target.value)} data-testid="terms-editor"
            className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm font-mono resize-y focus:outline-none" />
          <button onClick={saveTerms} className="w-full flex items-center justify-center gap-2 rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3" data-testid="terms-save"><Save size={16} /> {t("a_save_terms")}</button>
        </div>
      )}

      {tab === "android" && (
        <div className="space-y-5" data-testid="admin-android">
          <p className="text-sm text-[var(--rc-muted)]">{lang === "fr"
            ? "Gérez l'application Android native (APK). Téléverser une nouvelle version notifie automatiquement tous les utilisateurs Android."
            : "Manage the native Android app (APK). Uploading a new version automatically notifies all Android users."}</p>

          {/* Upload a new version */}
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 space-y-3">
            <p className="text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{lang === "fr" ? "TÉLÉVERSER UNE NOUVELLE VERSION" : "UPLOAD A NEW VERSION"}</p>
            <div>
              <label className="text-xs text-[var(--rc-muted)]">{lang === "fr" ? "Numéro de version" : "Version number"}</label>
              <input value={andVersion} onChange={(e) => setAndVersion(e.target.value)} placeholder="1.4.0" data-testid="android-version-input"
                className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2 mt-1 focus:outline-none" />
            </div>
            <input id="apk-file-input" type="file" accept=".apk,application/vnd.android.package-archive" className="hidden" data-testid="android-apk-file"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadApk(f); }} />
            <button onClick={() => document.getElementById("apk-file-input")?.click()} disabled={andBusy || !andVersion.trim()} data-testid="android-upload-btn"
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-50">
              {andBusy ? <><Loader2 size={16} className="animate-spin" /> {andProgress}%</> : <><Paperclip size={16} /> {lang === "fr" ? "Choisir un APK et publier" : "Choose APK & publish"}</>}
            </button>
            {andBusy && (
              <div className="h-1.5 w-full rounded-full bg-[var(--rc-border)] overflow-hidden">
                <div className="h-full bg-[var(--rc-gold)] transition-all" style={{ width: `${andProgress}%` }} />
              </div>
            )}
            <p className="text-xs text-[var(--rc-muted)] flex items-center gap-1.5"><Bell size={13} className="text-[var(--rc-gold)]" /> {lang === "fr"
              ? "À la publication, tous les utilisateurs Android reçoivent une notification de mise à jour."
              : "On publish, all Android users get an update notification."}</p>
          </div>

          {/* Version history */}
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
            <p className="text-xs font-semibold tracking-widest text-[var(--rc-muted)] mb-3">{lang === "fr" ? "HISTORIQUE DES VERSIONS" : "VERSION HISTORY"}</p>
            {andVers === null ? (
              <div className="flex items-center gap-2 text-sm text-[var(--rc-muted)]"><Loader2 size={16} className="animate-spin" /> …</div>
            ) : andVers.versions.length === 0 ? (
              <div className="text-sm text-[var(--rc-muted)]" data-testid="android-none">{lang === "fr" ? "Aucun APK téléversé pour l'instant." : "No APK uploaded yet."}</div>
            ) : (
              <div className="space-y-2" data-testid="android-versions">
                {andVers.versions.map((v) => (
                  <div key={v.id} data-testid={`android-version-${v.version}`}
                    className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${v.active ? "border-[var(--rc-gold)]/50 bg-[var(--rc-gold)]/5" : "border-[var(--rc-border)]"}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">v{v.version}</span>
                        {v.active && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[var(--rc-gold)] text-black inline-flex items-center gap-1" data-testid="android-live-badge"><Check size={11} /> {lang === "fr" ? "En ligne" : "Live"}</span>}
                      </div>
                      <div className="text-xs text-[var(--rc-muted)] flex items-center gap-2 mt-0.5">
                        <span>{fmtBytes(v.size)}</span>
                        <span className="inline-flex items-center gap-1" data-testid={`android-downloads-${v.version}`}><Download size={12} /> {v.downloads} {lang === "fr" ? "téléch." : "downloads"}</span>
                        {v.uploaded_at && <span className="hidden sm:inline">· {new Date(v.uploaded_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {v.active ? (
                        <a href={`${BACKEND}/api/app/android/apk`} target="_blank" rel="noreferrer" data-testid="android-open-live"
                          className="h-8 w-8 grid place-items-center rounded-lg border border-[var(--rc-border)] text-[var(--rc-muted)] hover:text-[var(--rc-gold)]" title={lang === "fr" ? "Ouvrir" : "Open"}><ExternalLink size={14} /></a>
                      ) : (
                        <button onClick={() => activateVer(v.id)} data-testid={`android-activate-${v.version}`}
                          className="text-xs rounded-lg px-2.5 py-1.5 border border-[var(--rc-gold)]/40 text-[var(--rc-gold)] hover:bg-[var(--rc-gold)]/10 inline-flex items-center gap-1.5" title={lang === "fr" ? "Rendre active" : "Make live"}><RotateCcw size={13} /> {lang === "fr" ? "Activer" : "Make live"}</button>
                      )}
                      <button onClick={() => deleteVer(v.id)} data-testid={`android-delete-${v.version}`}
                        className="h-8 w-8 grid place-items-center rounded-lg border border-[var(--rc-border)] text-[var(--rc-muted)] hover:text-red-400 hover:border-red-500/40" title={lang === "fr" ? "Supprimer" : "Delete"}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "ringtones" && <RingtonesAdmin />}

      {tab === "cleanup" && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
            <p className="text-sm font-medium mb-3">{t("a_free_space")}</p>

            <label className="text-xs text-[var(--rc-muted)]">{t("a_what_delete")}</label>
            <div className="flex items-center gap-2 mt-1 mb-2">
              <button onClick={() => setCleanup({ ...cleanup, types: [...MEDIA_TYPES] })} className="text-[11px] rounded-full px-2.5 py-1 border border-[var(--rc-border)] text-[var(--rc-muted)] hover:text-[var(--rc-gold)]" data-testid="cleanup-select-all">{t("a_select_all")}</button>
              <button onClick={() => setCleanup({ ...cleanup, types: [] })} className="text-[11px] rounded-full px-2.5 py-1 border border-[var(--rc-border)] text-[var(--rc-muted)] hover:text-[var(--rc-gold)]" data-testid="cleanup-clear">{t("a_clear")}</button>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {MEDIA_TYPES.map((tt) => <button key={tt} onClick={() => toggleType(cleanup, setCleanup, tt)} data-testid={`cleanup-type-${tt}`} className={`text-xs rounded-full px-3 py-1.5 border ${cleanup.types.includes(tt) ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>{typeLabel(tt)}</button>)}
            </div>

            <label className="text-xs text-[var(--rc-muted)]">{t("a_scope")}</label>
            <div className="grid grid-cols-2 gap-2 mt-1 mb-3">
              <button onClick={() => setCleanup({ ...cleanup, mode: "age" })} data-testid="cleanup-mode-age" className={`rounded-xl px-3 py-2 text-sm border ${cleanup.mode === "age" ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>{t("a_older_than")}</button>
              <button onClick={() => setCleanup({ ...cleanup, mode: "all" })} data-testid="cleanup-mode-all" className={`rounded-xl px-3 py-2 text-sm border ${cleanup.mode === "all" ? "border-red-500/60 text-red-400 bg-red-500/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>{t("a_delete_all")}</button>
            </div>
            {cleanup.mode === "age" && (
              <>
                <label className="text-xs text-[var(--rc-muted)]">{t("a_delete_older_days")}</label>
                <input type="number" min={0} value={cleanup.older_than_days} onChange={(e) => setCleanup({ ...cleanup, older_than_days: +e.target.value })} className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2 mt-1 mb-3" data-testid="cleanup-days" />
              </>
            )}
            {cleanup.mode === "all" && (
              <p className="text-xs text-red-400 mb-3">{t("a_delete_all_warn")}</p>
            )}

            <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={cleanup.delete_files} onChange={(e) => setCleanup({ ...cleanup, delete_files: e.target.checked })} data-testid="cleanup-delete-files" /> {t("a_also_delete_files")}</label>
            <button onClick={runCleanup} disabled={busy || cleanup.types.length === 0} className="w-full rounded-xl bg-red-500/15 text-red-400 border border-red-500/30 py-2.5 font-medium disabled:opacity-50" data-testid="run-cleanup-btn">{busy ? t("a_running") : cleanup.mode === "all" ? t("a_delete_all_now") : t("a_run_cleanup")}</button>
          </div>

          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium">{t("a_sched_cleanup")}</p>
              <button onClick={() => setSched({ ...sched, enabled: !sched.enabled })} data-testid="sched-toggle" className={`relative h-6 w-11 rounded-full ${sched.enabled ? "bg-[var(--rc-gold)]" : "bg-[var(--rc-surface-2)]"}`}><span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${sched.enabled ? "translate-x-5" : "translate-x-0.5"}`} /></button>
            </div>
            <label className="text-xs text-[var(--rc-muted)]">{t("a_autodelete_days")}</label>
            <input type="number" value={sched.days} onChange={(e) => setSched({ ...sched, days: +e.target.value })} className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2 mt-1 mb-3" data-testid="sched-days" />
            <div className="flex flex-wrap gap-2 mb-3">
              {MEDIA_TYPES.map((tt) => <button key={tt} onClick={() => toggleType(sched, setSched, tt)} className={`text-xs rounded-full px-3 py-1.5 border ${sched.types.includes(tt) ? "border-[var(--rc-gold)] text-[var(--rc-gold)]" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>{typeLabel(tt)}</button>)}
            </div>
            <button onClick={saveSchedule} className="w-full flex items-center justify-center gap-2 rounded-xl border border-[var(--rc-gold)]/40 text-[var(--rc-gold)] py-2.5" data-testid="save-sched-btn"><Save size={15} /> {t("a_save_schedule")}</button>
          </div>

          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4">
            <p className="text-sm font-medium mb-2">{t("a_custom_invite")}</p>
            <textarea value={invite} onChange={(e) => setInvite(e.target.value)} rows={3} className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2 text-sm" data-testid="invite-msg-input" />
            <button onClick={saveInvite} className="w-full mt-2 flex items-center justify-center gap-2 rounded-xl border border-[var(--rc-gold)]/40 text-[var(--rc-gold)] py-2.5" data-testid="save-invite-btn"><Save size={15} /> {t("a_save_message")}</button>
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="admin-user-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setSelectedUserId(null); setPwUserId(null); setPermUserId(null); }} />
          <div className="relative w-full sm:max-w-md bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-t-3xl sm:rounded-3xl p-5 max-h-[90vh] overflow-y-auto">
            <button onClick={() => { setSelectedUserId(null); setPwUserId(null); setPermUserId(null); }} className="absolute right-4 top-4 text-[var(--rc-muted)] hover:text-white" data-testid="admin-user-modal-close"><X size={20} /></button>
            <div className="flex flex-col items-center text-center pt-1 pb-4">
              <img src={avatarUrl(selectedUser.id)} alt="" className="h-20 w-20 rounded-full object-cover border-2 border-[var(--rc-gold)]/60 bg-[var(--rc-bg)]" />
              <div className="mt-3 text-lg font-semibold flex items-center gap-1.5">{selectedUser.name}{selectedUser.role === "admin" && <ShieldCheck size={16} className="text-[var(--rc-gold)]" />}</div>
              <div className="text-xs text-[var(--rc-muted)] flex items-center gap-1.5 mt-1">
                <span className={`h-2 w-2 rounded-full ${selectedUser.online ? "bg-emerald-500" : "bg-[var(--rc-muted)]"}`} />
                {selectedUser.online ? t("a_online") : t("a_offline")}
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-[var(--rc-border)] mb-4 text-sm divide-y divide-[var(--rc-border)]">
              <div className="flex items-center justify-between gap-3 bg-[var(--rc-bg)] px-4 py-3"><span className="text-[var(--rc-muted)]">{t("a_email")}</span><span className="font-medium truncate" data-testid="admin-user-email">{selectedUser.email || "—"}</span></div>
              <div className="flex items-center justify-between gap-3 bg-[var(--rc-bg)] px-4 py-3"><span className="text-[var(--rc-muted)]">{t("a_phone")}</span><span className="font-medium truncate" data-testid="admin-user-phone">{selectedUser.phone || "—"}</span></div>
              <div className="flex items-center justify-between gap-3 bg-[var(--rc-bg)] px-4 py-3"><span className="text-[var(--rc-muted)]">{t("a_joined")}</span><span className="font-medium" data-testid="admin-user-joined">{fmtJoin(selectedUser.created_at)}</span></div>
              <div className="flex items-center justify-between gap-3 bg-[var(--rc-bg)] px-4 py-3"><span className="text-[var(--rc-muted)]">{t("a_status")}</span>
                <span className={`font-medium ${selectedUser.status === "suspended" ? "text-red-400" : "text-emerald-400"}`} data-testid="admin-user-status">{selectedUser.status === "suspended" ? t("a_suspended") : t("a_active")}</span>
              </div>
              <div className="flex items-center justify-between gap-3 bg-[var(--rc-bg)] px-4 py-3"><span className="text-[var(--rc-muted)]">{lang === "fr" ? "Appareil" : "Device"}</span>
                <span className="font-medium flex items-center gap-1.5" data-testid="admin-user-device">
                  {(selectedUser.platforms && selectedUser.platforms.length)
                    ? selectedUser.platforms.map((os) => <span key={os} className="inline-flex items-center gap-0.5 text-[var(--rc-gold)]"><Smartphone size={13} /> {os}</span>)
                    : <span className="text-[var(--rc-muted)]">{lang === "fr" ? "Web / navigateur" : "Web / browser"}</span>}
                </span>
              </div>
            </div>

            <div className="mb-4" data-testid="admin-user-activity">
              <div className="text-xs text-[var(--rc-muted)] mb-1.5">{t("a_activity")}</div>
              <div className="rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-bg)] px-4 py-3 mb-2 flex items-center justify-between gap-3 text-sm">
                <span className="text-[var(--rc-muted)]">{t("a_last_seen")}</span>
                <span className="font-medium text-right" data-testid="admin-user-lastseen">
                  {selectedUser.online ? t("a_online") : (userStats?.last_seen ? new Date(userStats.last_seen).toLocaleString() : (userStats ? t("a_never") : "…"))}
                </span>
              </div>
              <div className="rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-bg)] px-4 py-3 mb-2 flex items-center justify-between gap-3 text-sm">
                <span className="text-[var(--rc-muted)]">{lang === "fr" ? "Dernier appareil" : "Latest device / OS"}</span>
                <span className="font-medium text-right flex items-center gap-1.5" data-testid="admin-user-os">
                  {userStats ? (userStats.last_os ? (<><Smartphone size={14} className="text-[var(--rc-gold)]" /> {userStats.last_os}{userStats.last_client ? ` · ${userStats.last_client}` : ""}</>) : (lang === "fr" ? "Inconnu" : "Unknown")) : "…"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[["a_messages", userStats?.messages, "admin-stat-messages"], ["a_orders", userStats?.orders, "admin-stat-orders"], ["a_posts", userStats?.posts, "admin-stat-posts"]].map(([k, v, tid]) => (
                  <div key={k} className="rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-bg)] py-3 text-center" data-testid={tid}>
                    <div className="text-lg font-semibold text-[var(--rc-gold)]">{userStats ? (v ?? 0) : "…"}</div>
                    <div className="text-[11px] text-[var(--rc-muted)]">{t(k)}</div>
                  </div>
                ))}
              </div>
            </div>


            <div className="mb-4">
              <div className="text-xs text-[var(--rc-muted)] mb-1.5">{t("a_role")}</div>
              {canManageRoles ? (
                <select value={selectedUser.role || "user"} onChange={(e) => updateUser(selectedUser.id, { role: e.target.value })} data-testid={`admin-role-${selectedUser.id}`} className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm">
                  <option value="user">user</option><option value="supervisor">supervisor</option><option value="manager">manager</option><option value="admin">admin</option>
                </select>
              ) : (
                <span className="inline-block text-sm rounded-lg bg-[var(--rc-bg)] border border-[var(--rc-border)] px-3 py-1.5 capitalize">{selectedUser.role || "user"}</span>
              )}
            </div>

            {canManageRoles && (selectedUser.role === "manager" || selectedUser.role === "supervisor") && (
              <div className="mb-4" data-testid={`admin-perms-row-${selectedUser.id}`}>
                <div className="text-xs text-[var(--rc-muted)] mb-1.5">{t("a_permissions")}</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {ADMIN_SECTIONS.map((sec) => {
                    const eff = allowedSections(selectedUser);
                    const checked = eff.includes(sec);
                    const toggle = () => { const next = checked ? eff.filter((x) => x !== sec) : [...eff, sec]; updateUser(selectedUser.id, { permissions: next }); };
                    return (
                      <label key={sec} className="flex items-center gap-2 text-xs rounded-lg bg-[var(--rc-bg)] border border-[var(--rc-border)] px-2 py-1.5 cursor-pointer" data-testid={`admin-perm-${selectedUser.id}-${sec}`}>
                        <input type="checkbox" checked={checked} onChange={toggle} className="accent-[var(--rc-gold)]" /> <span className="capitalize">{sec}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {pwUserId === selectedUser.id && (
              <div className="flex items-center gap-2 mb-3" data-testid={`admin-pw-row-${selectedUser.id}`}>
                <input type="text" autoFocus value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder={t("a_new_pw_ph")}
                  className="flex-1 bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" data-testid={`admin-pw-input-${selectedUser.id}`} />
                <button onClick={() => setUserPassword(selectedUser.id)} className="rounded-xl bg-[var(--rc-gold)] text-black px-3 py-2 text-sm font-semibold" data-testid={`admin-pw-save-${selectedUser.id}`}>{t("a_save")}</button>
                <button onClick={() => { setPwUserId(null); setPwValue(""); }} className="text-[var(--rc-muted)]"><X size={16} /></button>
              </div>
            )}

            <div className="grid grid-cols-1 gap-2">
              <button onClick={() => { setPwUserId(pwUserId === selectedUser.id ? null : selectedUser.id); setPwValue(""); }} className="flex items-center justify-center gap-2 rounded-xl border border-[var(--rc-border)] py-2.5 text-sm font-medium hover:border-[var(--rc-gold)]/40" data-testid={`admin-setpw-${selectedUser.id}`}><KeyRound size={16} /> {t("a_change_pw")}</button>
              {selectedUser.status === "suspended" ? (
                <button onClick={() => updateUser(selectedUser.id, { status: "active" })} className="flex items-center justify-center gap-2 rounded-xl border border-emerald-500/30 text-emerald-300 bg-emerald-500/10 py-2.5 text-sm font-medium" data-testid={`admin-activate-${selectedUser.id}`}><ShieldCheck size={16} /> {t("a_activate")}</button>
              ) : (
                <button onClick={() => updateUser(selectedUser.id, { status: "suspended" })} className="flex items-center justify-center gap-2 rounded-xl border border-amber-500/30 text-amber-300 bg-amber-500/10 py-2.5 text-sm font-medium" data-testid={`admin-suspend-${selectedUser.id}`}><Ban size={16} /> {t("a_suspend")}</button>
              )}
              <button onClick={() => { const id = selectedUser.id; delUser(id); setSelectedUserId(null); }} className="flex items-center justify-center gap-2 rounded-xl border border-red-500/30 text-red-400 bg-red-500/10 py-2.5 text-sm font-medium" data-testid={`admin-del-${selectedUser.id}`}><Trash2 size={16} /> {t("a_delete")}</button>
            </div>
          </div>
        </div>
      )}

      {bulkMsgOpen && (
        <div className="fixed inset-0 z-[72] flex items-end sm:items-center justify-center" data-testid="admin-bulk-msg-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setBulkMsgOpen(false)} />
          <div className="relative w-full sm:max-w-md bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-t-3xl sm:rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Send size={18} className="text-[var(--rc-gold)]" />
              <div className="font-semibold">{t("a_message_action")}</div>
              <span className="ml-auto text-xs text-[var(--rc-muted)]">{selectedIds.length} {t("a_selected")}</span>
            </div>
            <textarea value={bulkMsgText} onChange={(e) => setBulkMsgText(e.target.value)} rows={4} autoFocus placeholder={t("a_message_ph")} data-testid="admin-bulk-msg-input"
              className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
            <div className="flex items-center gap-2 mt-3">
              <button onClick={() => setBulkMsgOpen(false)} className="flex-1 rounded-xl border border-[var(--rc-border)] py-2.5 text-sm text-[var(--rc-muted)]">{t("a_cancel")}</button>
              <button onClick={sendBulkMessage} disabled={!bulkMsgText.trim()} data-testid="admin-bulk-msg-send" className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-[var(--rc-gold)] text-black py-2.5 text-sm font-semibold disabled:opacity-50"><Send size={15} /> {t("a_send")}</button>
            </div>
          </div>
        </div>
      )}


    </Modal>
  );
}
